import { Box } from "@mui/material";
import { type SxProps, type Theme, useTheme } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";

import { type PaperVariant } from "../../../ui-theme";
import { PageHeaderTopRow } from "../PageHeaderTopRow";
import { PageHeaderWrapper } from "../PageHeaderWrapper";
import { Text } from "../Text";
import { TextSkeleton } from "../TextSkeleton";

export interface StickyScrollPageHeaderProps {
  headerVariant?: PaperVariant;
  headerComponent?: React.ElementType;
  title: string;
  rightAction?: React.ReactNode;
  isLoading?: boolean;
  onClickBack?: () => void;
  hideBackButton?: boolean;
  navigateBack: () => void | Promise<void>;
  /**
   * If not provided, the parent element will be used as the scroll container
   */
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  LinkComponent: React.ElementType;
  /** Whether the title should wrap to multiple lines. Default is false (ellipsis). */
  shouldWrapTitle?: boolean;
  background?: (theme: Theme) => string | undefined;
}

// This is exported here because depending on isLoading flag we render either a skeleton or a text
const getTextSx = (isCollapsed: boolean): SxProps<Theme> => ({
  flexGrow: 1,
  textAlign: isCollapsed ? "center" : "left",
  overflow: "hidden",
  order: isCollapsed ? 0 : 1, // Change order based on collapsed state
  width: isCollapsed ? undefined : "100%", // Take full width when expanded
});

/**
 * A sticky header component that collapses/expands based on parent container scroll position.
 *
 * The header starts in an expanded state and collapses as the user scrolls down.
 * It features smooth transitions for both the header height and title text,
 * including fade animations and position adjustments.
 *
 * @behavior
 * - The header is sticky and remains at the top of its scrolling container
 * - Collapse transition triggers when scrolling past half of the collapsed height
 * - Text smoothly transitions between expanded (h4) and collapsed (h6) sizes
 * - Includes fade animations when transitioning between states
 * - Supports loading states with skeleton placeholder
 * - Maintains proper text overflow handling
 */
export function StickyScrollPageHeader(props: StickyScrollPageHeaderProps) {
  const {
    title,
    headerVariant = "primary",
    headerComponent = "header",
    rightAction,
    isLoading,
    onClickBack,
    scrollContainerRef,
    hideBackButton,
    navigateBack,
    LinkComponent,
    shouldWrapTitle = false,
    background,
  } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const [collapseState, setCollapseState] = useState<"initial" | "collapsed" | "expanded">(
    "initial"
  );

  const theme = useTheme();

  const isCollapsed = collapseState === "collapsed";

  const collapsedHeight = useRef(0);
  const heightDifference = useRef(0);

  useEffect(() => {
    if (titleRef.current && wrapperRef.current) {
      heightDifference.current =
        titleRef.current.getBoundingClientRect().height + Number.parseFloat(theme.spacing(5));
    }
  }, [theme, titleRef]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current ?? wrapperRef?.current?.parentElement;
    const headerWrapper = wrapperRef.current;

    if (!scrollContainer || !headerWrapper) {
      return;
    }

    if (isCollapsed) {
      collapsedHeight.current = headerWrapper.getBoundingClientRect().height;
    }

    const handleScroll = () => {
      const { scrollTop } = scrollContainer;
      const isCurrentlyCollapsed = scrollTop >= heightDifference.current;

      if (isCollapsed !== isCurrentlyCollapsed) {
        setCollapseState(isCurrentlyCollapsed ? "collapsed" : "expanded");
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainerRef, isCollapsed]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current ?? wrapperRef?.current?.parentElement;

    if (!scrollContainer) {
      return;
    }

    const paddingTop = isCollapsed ? `${heightDifference.current}px` : "0px";
    scrollContainer.style.setProperty("padding-top", paddingTop);
  }, [isCollapsed, scrollContainerRef]);

  const textVariant = isCollapsed ? "h6" : "h4";

  return (
    <PageHeaderWrapper
      ref={wrapperRef}
      variant={headerVariant}
      component={headerComponent}
      sx={{
        "& > *": {
          overflow: "hidden",
        },
        background,
      }}
    >
      <PageHeaderTopRow
        LinkComponent={LinkComponent}
        hideBackButton={hideBackButton}
        sx={{
          boxSizing: "content-box",
          flexWrap: isCollapsed ? "nowrap" : "wrap",
          alignItems: "center",
        }}
        title={
          <Box ref={titleRef} sx={{ ...getTextSx(isCollapsed) }}>
            {isLoading ? (
              <TextSkeleton variant={textVariant} width="30%" sx={getTextSx(isCollapsed)} />
            ) : (
              <Text
                semibold
                variant={textVariant}
                sx={{
                  ...(collapseState !== "initial" && {
                    animationDuration: `${theme.transitions.duration.standard}ms`,
                    animationTimingFunction: theme.transitions.easing.easeInOut,
                    animationName: isCollapsed ? "collapse" : "expand",
                    animationFillMode: "forwards",
                  }),
                  whiteSpace: shouldWrapTitle ? "normal" : "nowrap",
                  textOverflow: shouldWrapTitle ? "clip" : "ellipsis",
                  overflow: shouldWrapTitle ? "visible" : "hidden",
                  textWrap: shouldWrapTitle ? "balance" : "nowrap",

                  "@keyframes collapse": {
                    "0%": { opacity: 0 },
                    "100%": { opacity: 1 },
                  },

                  "@keyframes expand": {
                    "0%": { opacity: 0 },
                    "100%": { opacity: 1 },
                  },
                }}
              >
                {title}
              </Text>
            )}
          </Box>
        }
        rightAction={rightAction}
        isLoading={isLoading}
        navigateBack={navigateBack}
        onClickBack={onClickBack}
      />
    </PageHeaderWrapper>
  );
}
